import { useEffect, useRef, useState } from "react";
import { FaCheck, FaTimes } from "react-icons/fa";
import { useDispatch, useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import FromLayout from "../../../component/FromLayout";
import { usePaymentCompleteMutation } from "../../../store/api/admin/paymentSlice";
import { logOutTempUser } from "../../../store/reducer/UserReducer";

const PaymentComplete = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { search } = useLocation();
  const { tempData } = useSelector((state) => state.UserReducer);

  const queryParams = new URLSearchParams(search);

  const resultCode = queryParams.get("resultCode");
  const cardType = queryParams.get("cardType");
  const transactionId = queryParams.get("transactionId");
  const referenceId = queryParams.get("referenceId");
  const amount = queryParams.get("amount");
  const orderNumber = queryParams.get("orderNumber");
  const paymentMethodId = queryParams.get("paymentMethodId");
  const completeToken = queryParams.get("ct");

  const { user } = useSelector((state) => state.UserReducer);
  const role = user?.role === "super_admin" ? "admin" : user?.role;

  // subscription
  const [paymentComplete, { isLoading }] = usePaymentCompleteMutation();

  // Omni already charged the card by the time this page loads (resultCode
  // comes from Omni's own redirect) — but our backend hasn't necessarily
  // recorded it yet. Track that separately so we never tell the parent to
  // log in (and risk them being bounced back to pay again) before our
  // backend has actually confirmed the payment.
  const [confirmed, setConfirmed] = useState(false);
  const [confirmError, setConfirmError] = useState(null);

  const handleSubmit = async () => {
    toast.dismiss();
    setConfirmError(null);

    const payload = {
      referenceId,
      paymentMethodId,
      resultCode: Number(resultCode),
      paymentStatus: "Completed",
      transactionId,
      completeToken,
    };

    try {
      let response = await paymentComplete(payload).unwrap();
      if (response.statusCode === 200) {
        setConfirmed(true);
      }
    } catch (err) {
      // Your card was already charged by Omni — do NOT send the parent back
      // to /payment here, that would charge them a second time. Surface the
      // failure and let them retry this same confirmation instead.
      setConfirmError(
        err?.data?.message ||
          "We couldn't confirm your payment with our system yet.",
      );
    }
  };

  // This page loads via a full top-level redirect from Omni, not an
  // in-app navigation — so the Redux store re-initializes from scratch and
  // tempData starts out null until redux-persist finishes rehydrating it
  // from localStorage a moment later. resultCode is already present from
  // the URL on the very first render, so without tempData in the dep array
  // this effect fired once too early (tempData still null), never fired
  // again once tempData actually arrived, and handleSubmit() — the only
  // thing that confirms the payment with our backend — never ran at all.
  const autoSubmittedRef = useRef(false);
  useEffect(() => {
    if (
      !autoSubmittedRef.current &&
      tempData &&
      tempData?.status === "payment_pending" &&
      resultCode === "0"
    ) {
      autoSubmittedRef.current = true;
      handleSubmit();
    }
  }, [resultCode, tempData]);

  const handleGoDashboard = () => {
    dispatch(logOutTempUser());
    navigate("/");
  };

  return (
    <FromLayout
    // title={`${t("parent")} & ${t("student")} ${t("registration")} ${t("fee")}`}
    >
      <div className="payment-complete">
        <div className="p-0">
          {resultCode === 0 || resultCode === "0" ? (
            <div className="payment-success parent">
              <div className="success-card">
                <div className="success-icon">
                  <FaCheck />
                </div>
                <h2>Payment Successful!</h2>
                <p>
                  Thank you for your payment. Your transaction has been
                  completed.
                </p>

                <div className="order-details">
                  <div className="detail-row">
                    <span>Order ID:</span>
                    <span>{orderNumber}</span>
                  </div>
                  <div className="detail-row">
                    <span>Amount Paid:</span>
                    <span>${amount / 100}</span>
                  </div>
                  <div className="detail-row">
                    <span>Payment Method:</span>
                    <span>{cardType}</span>
                  </div>
                </div>

                {confirmError && (
                  <p className="text-danger mb-3">
                    Your card was charged, but we couldn't confirm it with our
                    system yet. Please don't pay again — click Retry below.
                  </p>
                )}

                <div>
                  {confirmError ? (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={isLoading}
                      onClick={handleSubmit}
                    >
                      {isLoading ? "Retrying..." : "Retry"}
                    </button>
                  ) : (
                    <button
                      type="button"
                      className="btn btn-primary"
                      disabled={isLoading || !confirmed}
                      onClick={handleGoDashboard}
                    >
                      {isLoading || !confirmed
                        ? "Confirming your payment..."
                        : "Go to Login"}
                    </button>
                  )}
                </div>
              </div>
            </div>
          ) : (
            <div className="payment-success parent">
              <div className="success-card">
                <div className="success-icon failed-icon">
                  <FaTimes />
                </div>
                <h2>Payment Failed</h2>
                <p>
                  Unfortunately, your payment could not be processed. Please
                  check your card details or try again later.
                </p>

                <div className="order-details">
                  <div className="detail-row">
                    <span>Order ID:</span>
                    <span>{orderNumber}</span>
                  </div>
                </div>

                <div>
                  <button
                    type="button"
                    className="btn btn-primary btn-failed"
                    onClick={() => navigate("/payment")}
                  >
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </FromLayout>
  );
};

export default PaymentComplete;
